/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

// components/ConstructionScreen.tsx
import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { UnderConstruction } from '../types/systemConfig';


interface ConstructionScreenProps {
  onSecretKnock?: () => void;
}

const ConstructionScreen: React.FC<ConstructionScreenProps> = ({ onSecretKnock }) => {
  const [message, setMessage] = useState<string>(
    "We are currently making some updates, but will be back online soon."
  );
  const [inputBuffer, setInputBuffer] = useState<string>('');
  const SECRET_CODE = 'admin'; // Change this to your preferred secret code

  useEffect(() => {
    // Listen to under_construction document
    const underConstructionRef = doc(db, 'system_config', 'under_construction');
    const unsubscribe = onSnapshot(underConstructionRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data() as UnderConstruction;
        setMessage(
          data.message || 
          "We are currently making some updates, but will be back online soon."
        );
      }
    });

    return () => unsubscribe();
  }, []);

  // Listen for secret code being typed
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const newBuffer = (inputBuffer + e.key).slice(-SECRET_CODE.length);
      setInputBuffer(newBuffer);
      
      if (newBuffer.toLowerCase() === SECRET_CODE.toLowerCase()) {
        console.log('🔓 Secret code entered!');
        if (onSecretKnock) {
          onSecretKnock();
        }
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [inputBuffer, onSecretKnock]);

  return (
    <main className="login">
      <div className="sherpas">
        <div className="login-container">
          {/* Logo/Header */}
          <div className="flex relative flex-end" style={{ marginBottom: '30px', justifyContent: 'center' }}>
            {/* <img id="theguy" src={theguy} alt="Merch Sherpa Cartoon" width="auto" height="80" />
            <div className="relative" style={{marginLeft:'10px'}}>
              <div className="mouse mouse--white">Merch Sherpa <span>$</span>ales Portal</div>
              <div className="mouse">Merch Sherpa <span>$</span>ales Portal</div>
            </div> */}
          </div>

          {/* Construction Message Box */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            padding: '40px 30px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
            maxWidth: '500px',
            margin: '0 auto'
          }}>
            {/* Warning Icon */}
            <div style={{ marginBottom: '24px' }}>
              <svg 
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  margin: '0 auto',
                  color: '#f59e0b'
                }}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                />
              </svg>
            </div>

            {/* Title */}
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111827',
              marginBottom: '16px'
            }}>
              Under Maintenance
            </h2>

            {/* Message */}
            <p style={{
              fontSize: '16px',
              color: '#4b5563',
              lineHeight: '1.6',
              marginBottom: '24px',
              whiteSpace: 'pre-line'
            }}>
              {message}
            </p>

            {/* Thank you message */}
            <p style={{
              fontSize: '14px',
              color: '#9ca3af',
              fontStyle: 'italic'
            }}>
              Thank you for your patience!
            </p>
          </div>

          {/* Hidden hint for admins - only shows in dev console */}
          <div style={{ height: '1px', overflow: 'hidden', opacity: 0 }}>
            Secret code: {SECRET_CODE}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ConstructionScreen;